// frontend/src/pages/ClientPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguageStore } from '../store/useLanguageStore';
import { useUIStore } from '../store/useUIStore';
import { LanguageSelector } from '../components/LanguageSelector';
import { apiClient } from '../api/client';
import { AppSidebar } from '../components/AppSidebar';
import { Modal } from '../components/Modal';
import { DataErasureSection } from '../components/DataErasureSection';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, Key, Webhook, Loader2, Menu, LogOut, X, Plus, Trash2,
  Send, CheckCircle2, XCircle, PauseCircle, AlertTriangle,
  Copy, Eye, EyeOff, ChevronLeft, ChevronRight, RefreshCw,
  Crown, ShieldCheck, Globe, CreditCard,
  Code2, ExternalLink, ChevronDown, Zap, AlertCircle, BookOpen, BarChart3,
} from 'lucide-react';
import LogoHorizontal from '../assets/images/datiaFlow_horizontal.jpg';
import Isotipo from '../assets/images/datiaFlow_cuadrado.jpg';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

const WEBHOOK_EVENTS = ['pipeline.completed', 'pipeline.failed'];

const ITEMS_PER_PAGE = 5;
const TABS = ['plan', 'apikeys', 'webhooks', 'integration'];

const SectionHelp = ({ items }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open]);

  return (
    <React.Fragment>
      <button
        onClick={() => setOpen(true)}
        className="ml-2 p-1 rounded-lg text-slate-300 hover:text-amber-500 hover:bg-amber-50 transition-all duration-150"
        aria-label="Ayuda"
        title="Ayuda"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm.75 11.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm-.75-2a.75.75 0 01-.75-.75v-.5a.75.75 0 01.41-.67A2.25 2.25 0 107.75 7.5a.75.75 0 01-1.5 0 3.75 3.75 0 113.75 3.75.75.75 0 01-.75-.75v-.5z" />
        </svg>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-amber-500">
                    <path d="M10 1a9 9 0 100 18 9 9 0 000-18zm.75 13.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm-.75-2.5a.75.75 0 01-.75-.75v-.5a.75.75 0 01.41-.67A2.25 2.25 0 1007.75 7.5a.75.75 0 01-1.5 0 3.75 3.75 0 113.75 3.75.75.75 0 01-.75-.75v-.5z" />
                  </svg>
                </div>
                <h3 className="text-base font-black text-slate-800">{items[0]?.sectionTitle || 'Ayuda'}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-slate-100 rounded-lg transition">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            <div className="overflow-y-auto p-5 space-y-3">
              {items.map((item, idx) => (
                <div key={idx} className="flex gap-3">
                  <div className="w-1 rounded-full bg-indigo-200 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-black text-slate-700">{item.label}</p>
                    <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-slate-100 shrink-0">
              <button onClick={() => setOpen(false)}
                className="w-full py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-200 transition">
                {items[0]?.closeLabel || 'Cerrar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

// ── Integration Tab Components ────────────────────────────────────────

const CodeBlock = ({ code, lang }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  return (
    <div className="relative group">
      <div className="absolute top-2 right-2 flex items-center gap-2 z-10">
        {lang && (
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider bg-slate-800 px-1.5 py-0.5 rounded">
            {lang}
          </span>
        )}
        <button
          onClick={handleCopy}
          className={`p-1.5 rounded-lg transition text-xs font-bold ${copied
            ? 'bg-green-600 text-white'
            : 'bg-slate-700 text-slate-300 hover:text-white hover:bg-slate-600'}`}
        >
          {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
      <pre className="bg-slate-900 text-slate-100 rounded-xl p-4 pt-3 overflow-x-auto text-xs font-mono leading-relaxed whitespace-pre">
        {code}
      </pre>
    </div>
  );
};

const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        {Icon && <Icon className="w-4 h-4 text-indigo-500 shrink-0" />}
        <span className="flex-1 text-sm font-bold text-slate-700">{title}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

const IntegrationTab = ({ t, products }) => {
  const [activeSnippet, setActiveSnippet] = useState('curl');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002/api/v1';
  const externalBase = backendUrl.replace(/\/api\/v1\/?$/, '/api/v1/external');
  const swaggerUrl = `${externalBase}/docs`;

  const activePlan = products.find(p => p.status === 1);
  const features = activePlan?.features || {};

  const snippets = {
    curl: `curl -X POST "${externalBase}/pipelines/42/execute" \\
  -H "X-API-Key: dfk_your_api_key_here" \\
  -H "Content-Type: application/json"`,
    python: `import requests

response = requests.post(
    "${externalBase}/pipelines/42/execute",
    headers={"X-API-Key": "dfk_your_api_key_here"}
)
data = response.json()
print(f"Execution ID: {data['executionId']}")
print(f"Status: {data['status']}")`,
    nodejs: `const response = await fetch(
  "${externalBase}/pipelines/42/execute",
  {
    method: "POST",
    headers: {
      "X-API-Key": "dfk_your_api_key_here",
      "Content-Type": "application/json"
    }
  }
);
const data = await response.json();
console.log("Execution ID:", data.executionId);
console.log("Status:", data.status);`,
  };

  const webhookPayload = `{
  "event": "pipeline.completed",
  "timestamp": 1718280000000,
  "data": {
    "pipelineId": 42,
    "pipelineName": "ERP → MySQL",
    "executionId": 507,
    "severity": "SUCCESS",
    "rowsProcessed": 15230,
    "rowsFailed": 0,
    "mbProcessed": 4.2,
    "durationMs": 12340
  }
}`;

  const hmacVerifyPython = `import hmac, hashlib

def verify_signature(payload_body, signature, secret):
    expected = hmac.new(
        secret.encode(), payload_body.encode(), hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(f"sha256={expected}", signature)

# En tu endpoint:
# signature = request.headers["X-DatiaFlow-Signature"]
# verify_signature(request.body, signature, "your_webhook_secret")`;

  const hmacVerifyNode = `const crypto = require("crypto");

function verifySignature(payloadBody, signature, secret) {
  const expected = crypto
    .createHmac("sha256", secret)
    .update(payloadBody)
    .digest("hex");
  return signature === \`sha256=\${expected}\`;
}

// In your handler:
// const sig = req.headers["x-datiaflow-signature"];
// verifySignature(JSON.stringify(req.body), sig, "your_secret");`;

  return (
    <div className="space-y-6">

      {/* ── Getting Started ──────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
        <h3 className="text-sm font-black text-slate-800 flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-amber-500" />
          {t('client.integration.getting_started') || 'Primeros pasos'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              step: '1',
              title: t('client.integration.step1_title') || 'Crear una API Key',
              desc: t('client.integration.step1_desc') || 'Ve a la pestaña "API Keys" y crea una nueva key. Copia el valor — solo se muestra una vez.',
            },
            {
              step: '2',
              title: t('client.integration.step2_title') || 'Hacer tu primera llamada',
              desc: t('client.integration.step2_desc') || 'Usa el header X-API-Key con tu key para ejecutar un pipeline o consultar su estado.',
            },
            {
              step: '3',
              title: t('client.integration.step3_title') || 'Verificar el resultado',
              desc: t('client.integration.step3_desc') || 'Consulta GET /executions/{id} con el executionId recibido para ver filas procesadas y estado final.',
            },
          ].map(({ step, title, desc }) => (
            <div key={step} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-black mb-2">
                {step}
              </div>
              <p className="text-sm font-bold text-slate-700">{title}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Code Snippets ──────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
        <h3 className="text-sm font-black text-slate-800 flex items-center gap-2 mb-4">
          <Code2 className="w-4 h-4 text-indigo-500" />
          {t('client.integration.snippets_title') || 'Ejemplos de código'}
        </h3>
        <div className="flex items-center gap-1 mb-3">
          {['curl', 'python', 'nodejs'].map(lang => (
            <button
              key={lang}
              onClick={() => setActiveSnippet(lang)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${activeSnippet === lang
                ? 'bg-slate-800 text-white'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
            >
              {lang === 'nodejs' ? 'Node.js' : lang.charAt(0).toUpperCase() + lang.slice(1)}
            </button>
          ))}
        </div>
        <CodeBlock code={snippets[activeSnippet]} lang={activeSnippet} />
      </div>

      {/* ── Endpoint Reference ──────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
        <h3 className="text-sm font-black text-slate-800 flex items-center gap-2 mb-4">
          <BookOpen className="w-4 h-4 text-blue-500" />
          {t('client.integration.endpoints_title') || 'Referencia de endpoints'}
        </h3>
        <div className="space-y-2">
          <CollapsibleSection title="POST /pipelines/{id}/execute" icon={Zap} defaultOpen={true}>
            <p className="text-xs text-slate-500">{t('client.integration.ep_execute_desc') || 'Dispara la ejecución de un pipeline. Retorna un executionId para consultar el resultado.'}</p>
            <div className="mt-2 text-xs">
              <p className="font-bold text-slate-600 mb-1">{t('client.integration.response') || 'Respuesta'}:</p>
              <CodeBlock code={`{
  "executionId": 507,
  "pipelineId": 42,
  "pipelineName": "ERP → MySQL",
  "status": "queued",
  "queuedAt": 1718280000000
}`} lang="json" />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="GET /pipelines/{id}/status">
            <p className="text-xs text-slate-500">{t('client.integration.ep_status_desc') || 'Estado actual del pipeline + último resultado de ejecución real.'}</p>
            <div className="mt-2 text-xs">
              <p className="font-bold text-slate-600 mb-1">{t('client.integration.response') || 'Respuesta'}:</p>
              <CodeBlock code={`{
  "pipelineId": 42,
  "pipelineName": "ERP → MySQL",
  "isRunning": false,
  "syncMode": "upsert",
  "frequencyType": "daily",
  "lastExecution": {
    "executionId": 507,
    "severity": "SUCCESS",
    "rowsProcessed": 15230,
    "rowsFailed": 0,
    "durationMs": 12340
  }
}`} lang="json" />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="GET /pipelines">
            <p className="text-xs text-slate-500">{t('client.integration.ep_list_desc') || 'Lista todos los pipelines activos de tu cuenta.'}</p>
            <div className="mt-2 text-xs">
              <p className="font-bold text-slate-600 mb-1">{t('client.integration.response') || 'Respuesta'}:</p>
              <CodeBlock code={`{
  "count": 3,
  "pipelines": [
    {
      "pipelineId": 10,
      "name": "ERP → MySQL",
      "syncMode": "upsert",
      "frequencyType": "daily",
      "isRunning": false,
      "lastExecutionAt": 1718280000000,
      "status": 1
    }
  ]
}`} lang="json" />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="GET /executions/{id}">
            <p className="text-xs text-slate-500">{t('client.integration.ep_detail_desc') || 'Detalle de una ejecución: filas procesadas, filas fallidas, MB transferidos, duración y errores.'}</p>
            <div className="mt-2 text-xs">
              <p className="font-bold text-slate-600 mb-1">{t('client.integration.response') || 'Respuesta'}:</p>
              <CodeBlock code={`{
  "executionId": 139,
  "pipelineId": 10,
  "pipelineName": "ERP → MySQL",
  "executionType": "api",
  "severity": "SUCCESS",
  "rowsProcessed": 15230,
  "rowsFailed": 0,
  "mbProcessed": 4.21,
  "durationMs": 12340,
  "errorMessage": null,
  "createdAt": 1718280000
}`} lang="json" />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="POST /projects/{id}/runs">
            <p className="text-xs text-slate-500">{t('client.integration.ep_project_run_desc') || 'Inicia un run de proyecto (ensayo o cutover). Orquesta todas las fases y pipelines asignados automáticamente.'}</p>
            <div className="mt-2 text-xs">
              <p className="font-bold text-slate-600 mb-1">Request body:</p>
              <CodeBlock code={`{
  "runType": "rehearsal",
  "runLabel": "Ensayo pre-cutover",
  "notes": "Validar datos maestros"
}`} lang="json" />
              <p className="font-bold text-slate-600 mb-1 mt-3">{t('client.integration.response') || 'Respuesta'}:</p>
              <CodeBlock code={`{
  "runId": 9,
  "projectId": 3,
  "projectName": "Migración SAP → MySQL",
  "runType": "rehearsal",
  "status": "running",
  "startedAt": 1718280000000
}`} lang="json" />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="GET /projects/{id}/runs/{runId}/status">
            <p className="text-xs text-slate-500">{t('client.integration.ep_project_status_desc') || 'Estado del run de proyecto con resumen de fases: pendientes, en ejecución, completadas y fallidas.'}</p>
            <div className="mt-2 text-xs">
              <p className="font-bold text-slate-600 mb-1">{t('client.integration.response') || 'Respuesta'}:</p>
              <CodeBlock code={`{
  "runId": 9,
  "projectId": 3,
  "projectName": "Migración SAP → MySQL",
  "runType": "rehearsal",
  "status": "completed",
  "startedAt": 1718280000000,
  "completedAt": 1718283600000,
  "phases": [
    {
      "idProjectPhase": 1,
      "name": "Datos básicos",
      "phaseOrder": 1,
      "status": "completed",
      "pipelineCount": 3
    },
    {
      "idProjectPhase": 2,
      "name": "Maestros",
      "phaseOrder": 2,
      "status": "completed",
      "pipelineCount": 5
    }
  ]
}`} lang="json" />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="GET /projects">
            <p className="text-xs text-slate-500">{t('client.integration.ep_projects_list_desc') || 'Lista todos los proyectos activos de tu cuenta con su estado actual.'}</p>
            <div className="mt-2 text-xs">
              <p className="font-bold text-slate-600 mb-1">{t('client.integration.response') || 'Respuesta'}:</p>
              <CodeBlock code={`{
  "count": 2,
  "projects": [
    {
      "projectId": 3,
      "name": "Migración SAP → MySQL",
      "status": "active",
      "phaseCount": 3,
      "cutoverMode": "scheduled",
      "cutoverAt": 1719100800000,
      "createdAt": 1717200000000
    }
  ]
}`} lang="json" />
            </div>
          </CollapsibleSection>
        </div>
      </div>

      {/* ── Webhooks ──────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
        <h3 className="text-sm font-black text-slate-800 flex items-center gap-2 mb-4">
          <Webhook className="w-4 h-4 text-purple-500" />
          {t('client.integration.webhooks_title') || 'Webhooks'}
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          {t('client.integration.webhooks_desc') || 'Cuando un pipeline termina, DatiaFlow envía un POST a tu URL con firma HMAC-SHA256.'}
        </p>

        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{t('client.integration.payload_example') || 'Ejemplo de payload'}</p>
        <CodeBlock code={webhookPayload} lang="json" />

        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-4 mb-2">{t('client.integration.verify_signature') || 'Verificar firma'}</p>
        <div className="space-y-2">
          <CodeBlock code={hmacVerifyPython} lang="python" />
          <CodeBlock code={hmacVerifyNode} lang="node.js" />
        </div>
        <p className="text-xs text-slate-400 mt-2">
          {t('client.integration.signature_header') || 'Header de firma'}: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-600 font-mono">X-DatiaFlow-Signature</code>
        </p>
      </div>

      {/* ── Swagger Link ─────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
              <ExternalLink className="w-4 h-4 text-green-500" />
              {t('client.integration.swagger_title') || 'Documentación interactiva (Swagger)'}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {t('client.integration.swagger_desc') || 'Explora y prueba todos los endpoints directamente desde el navegador.'}
            </p>
          </div>

          <a href={swaggerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2.5 rounded-xl font-bold text-xs hover:bg-slate-900 transition-all shrink-0"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {t('client.integration.open_swagger') || 'Abrir Swagger'}
          </a>
        </div>
      </div>

      {/* ── Error Reference ──────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-slate-100">
          <h3 className="text-sm font-black text-slate-800 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500" />
            {t('client.integration.errors_title') || 'Errores comunes'}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <th className="text-left px-4 py-3">{t('client.integration.err_code') || 'Código'}</th>
                <th className="text-left px-4 py-3">{t('client.integration.err_cause') || 'Causa'}</th>
                <th className="text-left px-4 py-3">{t('client.integration.err_solution') || 'Solución'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs">
              <tr className="hover:bg-slate-50/50">
                <td className="px-4 py-3 font-mono font-bold text-red-500">401</td>
                <td className="px-4 py-3 text-slate-600">{t('client.integration.err_401_cause') || 'API Key inválida o no proporcionada'}</td>
                <td className="px-4 py-3 text-slate-500">{t('client.integration.err_401_fix') || 'Verifica el header X-API-Key. La key debe comenzar con "dfk_".'}</td>
              </tr>
              <tr className="hover:bg-slate-50/50">
                <td className="px-4 py-3 font-mono font-bold text-red-500">403</td>
                <td className="px-4 py-3 text-slate-600">{t('client.integration.err_403_cause') || 'Permiso insuficiente o API deshabilitada'}</td>
                <td className="px-4 py-3 text-slate-500">{t('client.integration.err_403_fix') || 'Verifica que tu API Key tenga el permiso requerido y que tu plan incluya acceso a la API externa.'}</td>
              </tr>
              <tr className="hover:bg-slate-50/50">
                <td className="px-4 py-3 font-mono font-bold text-red-500">404</td>
                <td className="px-4 py-3 text-slate-600">{t('client.integration.err_404_cause') || 'Pipeline o ejecución no encontrada'}</td>
                <td className="px-4 py-3 text-slate-500">{t('client.integration.err_404_fix') || 'Verifica el ID. Solo puedes acceder a pipelines de tu propia cuenta.'}</td>
              </tr>
              <tr className="hover:bg-slate-50/50">
                <td className="px-4 py-3 font-mono font-bold text-red-500">429</td>
                <td className="px-4 py-3 text-slate-600">{t('client.integration.err_429_cause') || 'Límite de ejecución alcanzado'}</td>
                <td className="px-4 py-3 text-slate-500">{t('client.integration.err_429_fix') || 'Espera a que terminen las ejecuciones actuales, respeta el cooldown entre ejecuciones, o verifica el límite diario de tu plan.'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Execution Limits (from plan) ─────────────────────── */}
      {activePlan && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6">
          <h3 className="text-sm font-black text-slate-800 flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            {t('client.integration.limits_title') || 'Límites de tu plan'}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: t('client.integration.limit_concurrent') || 'Ejecuciones simultáneas',
                value: features.maxConcurrentExecutions ?? '—',
              },
              {
                label: t('client.integration.limit_cooldown') || 'Cooldown (segundos)',
                value: features.pipelineCooldownSeconds ?? '—',
              },
              {
                label: t('client.integration.limit_daily') || 'Ejecuciones diarias',
                value: features.maxDailyExecutions ?? '—',
              },
              {
                label: t('client.integration.limit_rate') || 'Peticiones/minuto',
                value: '60',
              },
            ].map(({ label, value }) => (
              <div key={label} className="bg-slate-50 rounded-xl p-3 border border-slate-100 text-center">
                <p className="text-lg font-black text-slate-800">{value}</p>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


export const ClientPage = ({ user, setUser }) => {
  const { t, language } = useLanguageStore();
  const { openSidebar } = useUIStore();
  const navigate = useNavigate();

  // const isAdmin = user?.idRole === 1 || user?.idRole === 2;
  const isAdmin = Number(user?.role) === 1 || Number(user?.role) === 2;

  const EVENT_LABELS = {
    'pipeline.completed': t('client.webhooks.event_completed') || 'Pipeline completado',
    'pipeline.failed': t('client.webhooks.event_failed') || 'Pipeline fallido',
  };

  // ── Global state ──────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('plan');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientName, setClientName] = useState('');

  // ── Profile data ──────────────────────────────────────────────────
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [productPage, setProductPage] = useState(1);

  // ── API Keys ──────────────────────────────────────────────────────
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [showCreateKey, setShowCreateKey] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);
  const [createdKeyRaw, setCreatedKeyRaw] = useState(null);
  const [showRawKey, setShowRawKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [createKeyError, setCreateKeyError] = useState('');

  // ── Webhooks ──────────────────────────────────────────────────────
  const [webhooks, setWebhooks] = useState([]);
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [showCreateWh, setShowCreateWh] = useState(false);
  const [creatingWh, setCreatingWh] = useState(false);
  const [createWhError, setCreateWhError] = useState('');
  const [testingWh, setTestingWh] = useState(null);
  const [testResult, setTestResult] = useState(null);

  // ── Confirmation modal ────────────────────────────────────────────
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // ── Zod schemas ───────────────────────────────────────────────────
  const keySchema = useMemo(() => z.object({
    name: z.string().min(2, t('common.validation_error') || 'Campo obligatorio'),
  }), [t, language]);

  const whSchema = useMemo(() => z.object({
    url: z.string().url(t('client.webhooks.invalid_url') || 'URL inválida'),
    events: z.array(z.string()).min(1, t('client.webhooks.select_event') || 'Seleccione al menos un evento'),
  }), [t, language]);

  const keyForm = useForm({
    resolver: zodResolver(keySchema),
    defaultValues: { name: '' },
  });

  const whForm = useForm({
    resolver: zodResolver(whSchema),
    defaultValues: { url: '', events: ['pipeline.completed', 'pipeline.failed'] },
  });

  // ── Logout ────────────────────────────────────────────────────────
  const handleLogout = () => {
    localStorage.removeItem('vnext_auth_token');
    localStorage.removeItem('vnext_user');
    setUser(null);
    navigate('/login');
  };

  // ── Fetch profile + products ──────────────────────────────────────
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [profRes, prodRes] = await Promise.all([
        apiClient.get('/client/profile'),
        apiClient.get('/client/products'),
      ]);
      setProfile(profRes.data);
      setProducts(prodRes.data);
      setClientName(profRes.data.tradeName || profRes.data.name);
    } catch (err) {
      setError(err.message || t('common.error_server'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  // ── Fetch API keys ────────────────────────────────────────────────
  const fetchApiKeys = useCallback(async () => {
    setApiKeysLoading(true);
    try {
      const res = await apiClient.get('/api-keys/');
      setApiKeys(res.data);
    } catch (err) {
      setError(err.message || t('common.error_server'));
    } finally {
      setApiKeysLoading(false);
    }
  }, [t]);

  // ── Fetch webhooks ────────────────────────────────────────────────
  const fetchWebhooks = useCallback(async () => {
    setWebhooksLoading(true);
    try {
      const res = await apiClient.get('/webhooks/');
      setWebhooks(res.data);
    } catch (err) {
      setError(err.message || t('common.error_server'));
    } finally {
      setWebhooksLoading(false);
    }
  }, [t]);

  // ── Load tab data on switch ───────────────────────────────────────
  useEffect(() => {
    if (activeTab === 'apikeys') fetchApiKeys();
    if (activeTab === 'webhooks') fetchWebhooks();
  }, [activeTab, fetchApiKeys, fetchWebhooks]);

  // ── ESC close modals ──────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') {
        setShowCreateKey(false);
        setShowCreateWh(false);
        setCreatedKeyRaw(null);
        setTestResult(null);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // ── Create API Key ────────────────────────────────────────────────
  const openCreateKey = () => {
    keyForm.reset({ name: '' });
    setCreateKeyError('');
    setCreatedKeyRaw(null);
    setShowRawKey(false);
    setCopiedKey(false);
    setShowCreateKey(true);
  };

  const onCreateKeySubmit = async (data) => {
    setCreatingKey(true);
    setCreateKeyError('');
    try {
      const res = await apiClient.post('/api-keys/', { name: data.name.trim() });
      setCreatedKeyRaw(res.data);
      setShowCreateKey(false);
      fetchApiKeys();
    } catch (err) {
      setCreateKeyError(err.message || t('common.error_server'));
    } finally {
      setCreatingKey(false);
    }
  };

  const copyKey = async () => {
    if (!createdKeyRaw?.key) return;
    try {
      await navigator.clipboard.writeText(createdKeyRaw.key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 3000);
    } catch { /* noop */ }
  };

  // ── Revoke API Key ────────────────────────────────────────────────
  const revokeApiKey = async () => {
    if (!confirmTarget) return;
    setActionLoading(true);
    try {
      await apiClient.delete(`/api-keys/${confirmTarget.idApiKey}`);
      fetchApiKeys();
    } catch (err) {
      setError(err.message || t('common.error_server'));
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
      setConfirmTarget(null);
    }
  };

  // ── Create Webhook ────────────────────────────────────────────────
  const openCreateWh = () => {
    whForm.reset({ url: '', events: ['pipeline.completed', 'pipeline.failed'] });
    setCreateWhError('');
    setShowCreateWh(true);
  };

  const onCreateWhSubmit = async (data) => {
    setCreatingWh(true);
    setCreateWhError('');
    try {
      await apiClient.post('/webhooks/', { url: data.url.trim(), events: data.events });
      setShowCreateWh(false);
      fetchWebhooks();
    } catch (err) {
      setCreateWhError(err.message || t('common.error_server'));
    } finally {
      setCreatingWh(false);
    }
  };

  // ── Toggle webhook active ─────────────────────────────────────────
  const toggleWebhookActive = async (wh) => {
    try {
      await apiClient.put(`/webhooks/${wh.idWebhook}`, { isActive: wh.isActive === 1 ? 0 : 1 });
      fetchWebhooks();
    } catch (err) {
      setError(err.message || t('common.error_server'));
    }
  };

  // ── Delete Webhook ────────────────────────────────────────────────
  const deleteWebhook = async () => {
    if (!confirmTarget) return;
    setActionLoading(true);
    try {
      await apiClient.delete(`/webhooks/${confirmTarget.idWebhook}`);
      fetchWebhooks();
    } catch (err) {
      setError(err.message || t('common.error_server'));
    } finally {
      setActionLoading(false);
      setConfirmAction(null);
      setConfirmTarget(null);
    }
  };

  // ── Test Webhook ──────────────────────────────────────────────────
  const testWebhook = async (wh) => {
    setTestingWh(wh.idWebhook);
    setTestResult(null);
    try {
      const res = await apiClient.post(`/webhooks/${wh.idWebhook}/test`);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.message || t('common.error_server') });
    } finally {
      setTestingWh(null);
    }
  };

  // ── Confirm dispatcher ────────────────────────────────────────────
  const executeConfirm = () => {
    if (confirmAction === 'revoke_key') revokeApiKey();
    if (confirmAction === 'delete_webhook') deleteWebhook();
  };

  const getConfirmMessage = () => {
    if (confirmAction === 'revoke_key' && confirmTarget)
      return (t('client.apikeys.confirm_revoke') || '¿Revocar la API key "{name}"?').replace('{name}', confirmTarget.name);
    if (confirmAction === 'delete_webhook' && confirmTarget)
      return (t('client.webhooks.confirm_delete') || '¿Eliminar el webhook "{url}"?').replace('{url}', confirmTarget.url);
    return '';
  };

  // ── Format date ───────────────────────────────────────────────────
  const fmtDate = (unixMs) => {
    if (!unixMs) return '—';
    const d = new Date(typeof unixMs === 'number' && unixMs > 9999999999 ? unixMs : unixMs * 1000);
    return d.toLocaleDateString(language === 'en' ? 'en-US' : language === 'pt' ? 'pt-BR' : 'es-PE', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const fmtNumber = (n) => {
    if (!n && n !== 0) return '—';
    if (n > 99999) return t('common.unlimited') || 'Ilimitado';
    const loc = language === 'en' ? 'en-US' : language === 'pt' ? 'pt-BR' : 'es-PE';
    return n.toLocaleString(loc);
  };

  const fmtPrice = (price, currency) => {
    if (price == null) return '—';
    const loc = language === 'en' ? 'en-US' : language === 'pt' ? 'pt-BR' : 'es-PE';
    return `${currency} ${price.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // ── Product pagination ────────────────────────────────────────────
  const productTotalPages = Math.ceil(products.length / ITEMS_PER_PAGE);
  const paginatedProducts = products.slice((productPage - 1) * ITEMS_PER_PAGE, productPage * ITEMS_PER_PAGE);
  const activePlan = products.find(p => p.status === 1);

  // ── Tab labels with i18n ──────────────────────────────────────────
  const tabLabels = {
    plan: t('client.tabs.plan') || 'Plan',
    apikeys: t('client.tabs.apikeys') || 'API Keys',
    webhooks: t('client.tabs.webhooks') || 'Webhooks',
    integration: t('client.tabs.integration') || 'Integración',
  };

  const tabIcons = {
    plan: <CreditCard className="w-4 h-4" />,
    apikeys: <Key className="w-4 h-4" />,
    webhooks: <Webhook className="w-4 h-4" />,
    integration: <Code2 className="w-4 h-4" />,
  };

  // ── Webhook status badge ──────────────────────────────────────────
  const getWhBadge = (wh) => {
    if (wh.isActive === 0) return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
        <PauseCircle className="w-3 h-3" />{t('client.webhooks.status_paused') || 'Pausado'}
      </span>
    );
    if (wh.consecutiveFailures >= 5) return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-200">
        <AlertTriangle className="w-3 h-3" />{t('client.webhooks.status_failing') || 'Fallando'}
      </span>
    );
    if (wh.lastDeliveryStatus === 'success') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-600 border border-green-200">
        <CheckCircle2 className="w-3 h-3" />{t('client.webhooks.status_ok') || 'OK'}
      </span>
    );
    if (wh.lastDeliveryStatus === 'failed') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-500 border border-red-200">
        <XCircle className="w-3 h-3" />{t('client.webhooks.status_failed') || 'Fallido'}
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-50 text-slate-400 border border-slate-200">
        {t('client.webhooks.status_pending') || 'Sin envíos'}
      </span>
    );
  };

  // ══════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      <AppSidebar user={user} onLogout={handleLogout} />

      {/* ── Header IDÉNTICO a Dashboard ──────────────────────────── */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={openSidebar}
              aria-label="Abrir menú"
              className="p-2 rounded-xl text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Link to="/dashboard" className="flex items-center cursor-pointer group shrink-0">
              <img src={Isotipo} alt="DatiaFlow"
                className="block md:hidden h-9 w-9 object-cover object-left rounded-md group-hover:scale-105 transition-transform" />
              <img src={LogoHorizontal} alt="DatiaFlow"
                className="hidden md:block h-11 w-auto group-hover:opacity-90 transition-opacity" />
            </Link>
          </div>
          <div className="flex items-center space-x-6">
            <LanguageSelector />
            <div className="flex items-center space-x-3 border-l border-slate-200 pl-6">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-bold text-slate-700">{clientName}</p>
                <p className="text-xs text-slate-500">{user?.name}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                title={t('dashboard.logout')}
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ── Content ──────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── Page title ─────────────────────────────────────────── */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
            <Building2 className="w-5 h-5" />
          </div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-800">{t('client.title') || 'Cliente'}</h1>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm font-medium rounded-xl flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />{error}
            <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        ) : (
          <React.Fragment>
            {/* ── Profile Card ───────────────────────────────────── */}
            {profile && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="space-y-2">
                    <h2 className="text-lg font-black text-slate-800">{profile.name}</h2>
                    {profile.tradeName && profile.tradeName !== profile.name && (
                      <p className="text-sm text-slate-500">{profile.tradeName}</p>
                    )}
                    {profile.typeNumberDocument && (
                      <p className="text-xs text-slate-400">{t('client.profile.document') || 'Documento'}: {profile.typeNumberDocument}</p>
                    )}
                    {(profile.address1 || profile.address2) && (
                      <p className="text-xs text-slate-400">
                        {[profile.address1, profile.address2].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {profile.countryName && (
                      <p className="text-xs text-slate-400 flex items-center gap-1">
                        <Globe className="w-3 h-3" />{profile.countryName}
                      </p>
                    )}
                  </div>

                  {/* Plan badge */}
                  {profile.activePlanName && (
                    <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-2.5 shrink-0">
                      <Crown className="w-5 h-5 text-indigo-600" />
                      <div>
                        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">{t('client.profile.active_plan') || 'Plan activo'}</p>
                        <p className="text-sm font-black text-indigo-700">{profile.activePlanName}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Tabs ───────────────────────────────────────────── */}
            <div className="flex items-center gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${activeTab === tab
                    ? 'border-indigo-600 text-indigo-700'
                    : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300'
                    }`}
                >
                  {tabIcons[tab]}
                  {tabLabels[tab]}
                </button>
              ))}
            </div>

            {/* ═══════════════════════════════════════════════════════
                TAB: Plan
            ═══════════════════════════════════════════════════════ */}
            {activeTab === 'plan' && (
              <>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="p-4 sm:p-6 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-sm font-black text-slate-700 flex items-center">
                      {t('client.plan.history_title') || 'Historial de suscripciones'}
                      <SectionHelp items={[
                        {
                          sectionTitle: t('client.plan.history_title') || 'Historial de suscripciones', closeLabel: t('common.buttons.close') || 'Cerrar',
                          label: t('client.plan.col_plan') || 'Plan', desc: t('client.plan.tip_section') || 'Registro de todos los planes contratados y sus límites operativos. El plan activo define las cuotas vigentes para tu cuenta.'
                        },
                        { label: 'Pipelines', desc: t('client.plan.tip_pipelines') || 'Cantidad máxima de pipelines de migración que puedes crear.' },
                        { label: t('client.plan.col_users') || 'Usuarios', desc: t('client.plan.tip_users') || 'Cantidad máxima de usuarios que pueden acceder al sistema simultáneamente.' },
                        { label: 'MB', desc: t('client.plan.tip_mb') || 'Megabytes máximos por ejecución. Se mide el volumen de datos que llega exitosamente al destino.' },
                        { label: t('client.plan.col_records') || 'Registros', desc: t('client.plan.tip_records') || 'Registros máximos por ejecución. Se cuentan solo los escritos exitosamente en el destino.' },
                        { label: t('client.plan.col_cycle') || 'Ciclo', desc: t('client.plan.tip_cycle') || 'Frecuencia de facturación: mensual, anual o freemium.' },
                      ]} />
                    </h3>
                    <span className="text-xs text-slate-400">{products.length} {t('client.plan.records') || 'registros'}</span>
                  </div>

                  {products.length === 0 ? (
                    <div className="p-12 text-center text-slate-400 text-sm">{t('client.plan.no_records') || 'Sin registros de suscripción.'}</div>
                  ) : (
                    <React.Fragment>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                              <th className="text-left px-4 py-3">{t('client.plan.col_plan') || 'Plan'}</th>
                              <th className="text-left px-4 py-3">{t('client.plan.col_subscription') || 'Suscripción'}</th>
                              <th className="text-left px-4 py-3">{t('client.plan.col_expiration') || 'Expiración'}</th>
                              <th className="text-right px-4 py-3">{t('client.plan.col_price') || 'Precio'}</th>
                              <th className="text-center px-4 py-3">{t('client.plan.col_cycle') || 'Ciclo'}</th>
                              <th className="text-center px-4 py-3">Pipelines</th>
                              <th className="text-center px-4 py-3">{t('client.plan.col_users') || 'Usuarios'}</th>
                              <th className="text-center px-4 py-3">MB</th>
                              <th className="text-center px-4 py-3">{t('client.plan.col_records') || 'Registros'}</th>
                              <th className="text-center px-4 py-3">{t('client.plan.col_status') || 'Estado'}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {paginatedProducts.map((cp) => (
                              <tr key={cp.idClientProduct} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-4 py-3 font-bold text-slate-700">{cp.productName}</td>
                                <td className="px-4 py-3 text-slate-500">{fmtDate(cp.dateSubscription)}</td>
                                <td className="px-4 py-3 text-slate-500">{fmtDate(cp.dateExpiration)}</td>
                                <td className="px-4 py-3 text-right font-mono text-slate-700">{fmtPrice(cp.price, cp.currency)}</td>
                                <td className="px-4 py-3 text-center text-slate-500 capitalize">{cp.billingCycle}</td>
                                <td className="px-4 py-3 text-center text-slate-600 font-bold">{fmtNumber(cp.quantityPipeline)}</td>
                                <td className="px-4 py-3 text-center text-slate-600 font-bold">{fmtNumber(cp.quantityUser)}</td>
                                <td className="px-4 py-3 text-center text-slate-600 font-bold">{fmtNumber(cp.quantityTransferMB)}</td>
                                <td className="px-4 py-3 text-center text-slate-600 font-bold">{fmtNumber(cp.quantityRecord)}</td>
                                <td className="px-4 py-3 text-center">
                                  {cp.status === 1 ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-600 border border-green-200">
                                      <CheckCircle2 className="w-3 h-3" />{t('common.active') || 'Activo'}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200">
                                      {t('common.inactive') || 'Inactivo'}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {productTotalPages > 1 && (
                        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-slate-100">
                          <p className="text-xs text-slate-400">{products.length} {t('client.plan.records') || 'registros'}</p>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setProductPage(p => Math.max(1, p - 1))} disabled={productPage === 1}
                              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                              <ChevronLeft className="w-4 h-4 text-slate-500" />
                            </button>
                            {Array.from({ length: productTotalPages }, (_, i) => i + 1).map(page => (
                              <button key={page} onClick={() => setProductPage(page)}
                                className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${page === productPage ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'text-slate-500 hover:bg-slate-100'}`}>
                                {page}
                              </button>
                            ))}
                            <button onClick={() => setProductPage(p => Math.min(productTotalPages, p + 1))} disabled={productPage === productTotalPages}
                              className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </div>

                {/* ── OBS-09: TopValues Opt-In ── */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mt-4">
                  <div className="p-4 sm:p-6 flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-black text-slate-700 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-teal-500" />
                        {t('client.plan.top_values_title') || 'Visualización de datos reales en perfilado'}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-xl">
                        {t('client.plan.top_values_desc') || 'Cuando está activo, la función de perfilado (Data Profiling) almacena los valores más frecuentes (topValues) de sus datos. Estos valores podrían contener datos personales. Al activar esta opción, usted asume la responsabilidad de contar con base legal para su almacenamiento conforme a la normativa de protección de datos aplicable.'}
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 shrink-0">
                      <input type="checkbox" className="sr-only peer"
                        checked={!!activePlan?.features?.topValuesEnabled}
                        onChange={async (e) => {
                          try {
                            await apiClient.patch('/client/features/top-values', { enabled: e.target.checked });
                            fetchProfile();
                          } catch (err) {
                            setModalConfig({ isOpen: true, title: t('common.error'), message: err.message, showCancel: false, onAccept: closeModal });
                          }
                        }}
                      />
                      <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                    </label>
                  </div>
                </div>

                {/* ── Eliminación de Datos (Sprint H — GDPR Art. 17) ── */}
                <DataErasureSection userRole={user?.role} />
              </>
            )}

            {/* ═══════════════════════════════════════════════════════
                TAB: API Keys
            ═══════════════════════════════════════════════════════ */}
            {activeTab === 'apikeys' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-sm font-black text-slate-700 flex items-center">
                    {t('client.apikeys.title') || 'API Keys'}
                    <SectionHelp items={[
                      {
                        sectionTitle: t('client.apikeys.title') || 'API Keys', closeLabel: t('common.buttons.close') || 'Cerrar',
                        label: t('client.apikeys.title') || 'API Keys', desc: t('client.apikeys.tip_section') || 'Las API Keys permiten que sistemas externos ejecuten y consulten tus pipelines de migración de forma automática (machine-to-machine), sin necesidad de iniciar sesión en DatiaFlow.'
                      },
                      { label: t('client.apikeys.col_prefix') || 'Prefijo', desc: t('client.apikeys.tip_prefix') || 'Primeros caracteres visibles de la key (dfk_xxxx). La key completa solo se muestra al crearla.' },
                      { label: t('client.apikeys.col_permissions') || 'Permisos', desc: t('client.apikeys.tip_permissions') || '"Completo" significa acceso a ejecutar pipelines, consultar estado, listar pipelines y ver resultados.' },
                      { label: t('client.apikeys.col_last_used') || 'Último uso', desc: t('client.apikeys.tip_last_used') || 'Fecha de la última vez que un sistema externo utilizó esta key para acceder a la API.' },
                      { label: t('common.actions') || 'Acciones', desc: t('client.apikeys.tip_actions') || 'Revocar una key la deshabilita permanentemente. Los sistemas que la usen dejarán de tener acceso.' },
                    ]} />
                  </h3>
                  {isAdmin && (
                    <button onClick={openCreateKey}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl font-bold text-xs hover:bg-blue-700 transition-all shadow-sm">
                      <Plus className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{t('client.apikeys.create') || 'Crear API Key'}</span>
                    </button>
                  )}
                </div>

                {apiKeysLoading ? (
                  <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin" /></div>
                ) : apiKeys.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 text-sm">{t('client.apikeys.no_keys') || 'Sin API keys creadas.'}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          <th className="text-left px-4 py-3">{t('client.apikeys.col_name') || 'Nombre'}</th>
                          <th className="text-left px-4 py-3">{t('client.apikeys.col_prefix') || 'Prefijo'}</th>
                          <th className="text-center px-4 py-3">{t('client.apikeys.col_permissions') || 'Permisos'}</th>
                          <th className="text-left px-4 py-3">{t('client.apikeys.col_last_used') || 'Último uso'}</th>
                          <th className="text-left px-4 py-3">{t('client.apikeys.col_created') || 'Creada'}</th>
                          <th className="text-center px-4 py-3">{t('common.actions') || 'Acciones'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {apiKeys.map((k) => (
                          <tr key={k.idApiKey} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 font-bold text-slate-700">{k.name}</td>
                            <td className="px-4 py-3 font-mono text-xs text-slate-500">{k.keyPrefix}••••</td>
                            <td className="px-4 py-3 text-center">
                              {/* <span className="text-[10px] text-slate-500 font-bold">
                                {Object.entries(k.permissions || {}).filter(([, v]) => v).length} {t('client.apikeys.active_perms') || 'activos'}
                              </span> */}
                              {(() => {
                                const perms = k.permissions || {};
                                const active = Object.entries(perms).filter(([, v]) => v);
                                const total = Object.keys(perms).length;
                                if (active.length === total && total > 0) {
                                  return (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-600 border border-green-200">
                                      <ShieldCheck className="w-3 h-3" />
                                      {t('client.apikeys.perm_full') || 'Completo'}
                                    </span>
                                  );
                                }
                                return (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-600 border border-amber-200">
                                    {active.length}/{total}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs">{k.lastUsedAt ? fmtDate(k.lastUsedAt) : '—'}</td>
                            <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(k.createdAt)}</td>
                            <td className="px-4 py-3 text-center">
                              {isAdmin ? (
                                <button onClick={() => { setConfirmAction('revoke_key'); setConfirmTarget(k); }}
                                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                  title={t('client.apikeys.revoke') || 'Revocar'}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════
                TAB: Webhooks
            ═══════════════════════════════════════════════════════ */}
            {activeTab === 'webhooks' && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="text-sm font-black text-slate-700 flex items-center">
                    {t('client.webhooks.title') || 'Webhooks'}
                    <SectionHelp items={[
                      {
                        sectionTitle: t('client.webhooks.title') || 'Webhooks', closeLabel: t('common.buttons.close') || 'Cerrar',
                        label: t('client.webhooks.title') || 'Webhooks', desc: t('client.webhooks.tip_section') || 'Los webhooks envían notificaciones automáticas a una URL de tu sistema cuando un pipeline termina (exitoso o con error). Útil para automatizar flujos post-migración.'
                      },
                      { label: 'URL', desc: t('client.webhooks.tip_url') || 'Dirección web donde DatiaFlow enviará una solicitud POST cada vez que ocurra un evento suscrito.' },
                      { label: t('client.webhooks.col_events') || 'Eventos', desc: t('client.webhooks.tip_events') || '"Pipeline completado": se envía cuando termina con éxito. "Pipeline fallido": se envía cuando hay error.' },
                      { label: t('client.webhooks.col_status') || 'Estado', desc: t('client.webhooks.tip_status') || 'OK = último envío exitoso. Fallido = el endpoint respondió con error. Pausado = deshabilitado manualmente o por auto-pausa tras 10 fallos.' },
                      { label: t('client.webhooks.col_failures') || 'Fallos consecutivos', desc: t('client.webhooks.tip_failures') || 'Cantidad de envíos fallidos consecutivos. A los 10 fallos el webhook se pausa automáticamente y se envía un email al administrador.' },
                      { label: t('common.actions') || 'Acciones', desc: t('client.webhooks.tip_actions') || 'Pausar/activar el webhook, enviar un ping de prueba para verificar la conexión, o eliminar.' },
                    ]} />
                  </h3>
                  {isAdmin && (
                    <button onClick={openCreateWh}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl font-bold text-xs hover:bg-blue-700 transition-all shadow-sm">
                      <Plus className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{t('client.webhooks.create') || 'Crear Webhook'}</span>
                    </button>
                  )}
                </div>

                {webhooksLoading ? (
                  <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 text-indigo-500 animate-spin" /></div>
                ) : webhooks.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 text-sm">{t('client.webhooks.no_webhooks') || 'Sin webhooks configurados.'}</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          <th className="text-left px-4 py-3">URL</th>
                          <th className="text-left px-4 py-3">{t('client.webhooks.col_events') || 'Eventos'}</th>
                          <th className="text-center px-4 py-3">{t('client.webhooks.col_status') || 'Estado'}</th>
                          <th className="text-center px-4 py-3">{t('client.webhooks.col_failures') || 'Fallos'}</th>
                          <th className="text-left px-4 py-3">{t('client.webhooks.col_last_delivery') || 'Último envío'}</th>
                          <th className="text-center px-4 py-3">{t('common.actions') || 'Acciones'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {webhooks.map((wh) => (
                          <tr key={wh.idWebhook} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 font-mono text-xs text-slate-600 max-w-[200px] truncate" title={wh.url}>{wh.url}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(wh.events || []).map((ev) => (
                                  // <span key={ev} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-500">{ev}</span>
                                  <span key={ev} className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-500">
                                    {EVENT_LABELS[ev] || ev}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">{getWhBadge(wh)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`text-xs font-bold ${wh.consecutiveFailures > 0 ? 'text-red-500' : 'text-slate-400'}`}>
                                {wh.consecutiveFailures}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-500 text-xs">{wh.lastDeliveryAt ? fmtDate(wh.lastDeliveryAt) : '—'}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                {isAdmin ? (
                                  <React.Fragment>
                                    <button onClick={() => toggleWebhookActive(wh)}
                                      className={`p-1.5 rounded-lg transition ${wh.isActive === 1 ? 'text-amber-500 hover:bg-amber-50' : 'text-green-500 hover:bg-green-50'}`}
                                      title={wh.isActive === 1 ? (t('client.webhooks.pause') || 'Pausar') : (t('client.webhooks.activate') || 'Activar')}>
                                      {wh.isActive === 1 ? <PauseCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                    </button>
                                    <button onClick={() => testWebhook(wh)}
                                      disabled={testingWh === wh.idWebhook}
                                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-50"
                                      title={t('client.webhooks.test') || 'Test ping'}>
                                      {testingWh === wh.idWebhook ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    </button>
                                    <button onClick={() => { setConfirmAction('delete_webhook'); setConfirmTarget(wh); }}
                                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                      title={t('client.webhooks.delete') || 'Eliminar'}>
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </React.Fragment>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {/* ═══════════════════════════════════════════════════════
                TAB: Integration
            ═══════════════════════════════════════════════════════ */}
            {activeTab === 'integration' && (
              <IntegrationTab t={t} products={products} />
            )}
          </React.Fragment>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MODALS
      ═══════════════════════════════════════════════════════════════ */}

      {/* ── Create API Key Modal ──────────────────────────────────── */}
      {showCreateKey && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowCreateKey(false)}>
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-black text-slate-800">{t('client.apikeys.create') || 'Crear API Key'}</h3>
                </div>
                <button onClick={() => setShowCreateKey(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
            <form onSubmit={keyForm.handleSubmit(onCreateKeySubmit)} noValidate>
              <div className="p-6 space-y-4">
                {createKeyError && (
                  <div className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl">{createKeyError}</div>
                )}
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">
                    {t('client.apikeys.field_name') || 'Nombre descriptivo'} *
                  </label>
                  <input type="text" {...keyForm.register('name')} maxLength={100}
                    className={`w-full bg-slate-50 border-2 rounded-xl py-2.5 px-4 outline-none font-medium text-sm transition-all ${keyForm.formState.errors.name ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'}`}
                    placeholder={t('client.apikeys.name_placeholder') || 'Ej: ERP Integration, CI/CD Pipeline'} />
                  {keyForm.formState.errors.name && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{keyForm.formState.errors.name.message}</span>}
                </div>
              </div>
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                <button type="button" onClick={() => setShowCreateKey(false)}
                  className="px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-colors">
                  {t('common.buttons.cancel') || 'Cancelar'}
                </button>
                <button type="submit" disabled={creatingKey}
                  className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 transition-all disabled:bg-slate-300">
                  {creatingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  {t('client.apikeys.create') || 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Created Key Reveal Modal ──────────────────────────────── */}
      {createdKeyRaw && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setCreatedKeyRaw(null)}>
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                <h3 className="text-lg font-black text-slate-800">{t('client.apikeys.created_title') || 'API Key creada'}</h3>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs font-bold text-amber-700">{t('client.apikeys.created_warning') || '⚠️ Copia esta key ahora. No podrás verla de nuevo.'}</p>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">{t('client.apikeys.field_name') || 'Nombre'}</label>
                <p className="text-sm font-bold text-slate-700">{createdKeyRaw.name}</p>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">API Key</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-slate-100 rounded-xl px-4 py-2.5 font-mono text-xs text-slate-700 break-all select-all">
                    {showRawKey ? createdKeyRaw.key : '••••••••••••••••••••••••••••••••••••••••••••••'}
                  </code>
                  <button onClick={() => setShowRawKey(!showRawKey)}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition shrink-0">
                    {showRawKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={copyKey}
                    className={`p-2 rounded-lg transition shrink-0 ${copiedKey ? 'text-green-600 bg-green-50' : 'text-slate-400 hover:text-blue-600 hover:bg-blue-50'}`}>
                    {copiedKey ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end">
              <button onClick={() => setCreatedKeyRaw(null)}
                className="px-5 py-2.5 bg-slate-800 text-white rounded-xl font-bold text-sm hover:bg-slate-900 transition-all">
                {t('client.apikeys.understood') || 'Entendido'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Webhook Modal ──────────────────────────────────── */}
      {showCreateWh && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={() => setShowCreateWh(false)}>
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Webhook className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-black text-slate-800">{t('client.webhooks.create') || 'Crear Webhook'}</h3>
                </div>
                <button onClick={() => setShowCreateWh(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
            <form onSubmit={whForm.handleSubmit(onCreateWhSubmit)} noValidate>
              <div className="p-6 space-y-4">
                {createWhError && (
                  <div className="p-3 bg-red-50 text-red-600 text-xs font-bold rounded-xl">{createWhError}</div>
                )}
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">URL *</label>
                  <input type="url" {...whForm.register('url')}
                    className={`w-full bg-slate-50 border-2 rounded-xl py-2.5 px-4 outline-none font-medium text-sm transition-all ${whForm.formState.errors.url ? 'border-red-300 focus:border-red-500' : 'border-transparent focus:border-blue-100'}`}
                    placeholder="https://your-app.com/webhooks/datiaflow" />
                  {whForm.formState.errors.url && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{whForm.formState.errors.url.message}</span>}
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-2">
                    {t('client.webhooks.col_events') || 'Eventos'} *
                  </label>
                  <div className="space-y-2">
                    {WEBHOOK_EVENTS.map((ev) => (
                      <label key={ev} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" value={ev}
                          {...whForm.register('events')}
                          defaultChecked={true}
                          className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm text-slate-600 font-medium">{EVENT_LABELS[ev] || ev}</span>
                      </label>
                    ))}
                  </div>
                  {whForm.formState.errors.events && <span className="text-red-500 text-[10px] font-bold ml-2 mt-1 block">{whForm.formState.errors.events.message}</span>}
                </div>
              </div>
              <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                <button type="button" onClick={() => setShowCreateWh(false)}
                  className="px-4 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100 transition-colors">
                  {t('common.buttons.cancel') || 'Cancelar'}
                </button>
                <button type="submit" disabled={creatingWh}
                  className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 transition-all disabled:bg-slate-300">
                  {creatingWh ? <Loader2 className="w-4 h-4 animate-spin" /> : <Webhook className="w-4 h-4" />}
                  {t('client.webhooks.create') || 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Test Result Toast ─────────────────────────────────────── */}
      {testResult && (
        <div className="fixed bottom-6 right-6 z-[80] max-w-sm animate-in slide-in-from-bottom-4 duration-300">
          <div className={`rounded-2xl shadow-2xl border p-4 ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-start gap-3">
              {testResult.success ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {testResult.success ? (t('client.webhooks.test_success') || 'Ping exitoso') : (t('client.webhooks.test_failed') || 'Ping falló')}
                </p>
                <p className="text-xs text-slate-500 mt-1 break-all">{testResult.message}</p>
              </div>
              <button onClick={() => setTestResult(null)} className="p-1 hover:bg-white/50 rounded-lg shrink-0">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmation Modal ────────────────────────────────────── */}
      <Modal
        isOpen={!!confirmAction}
        title={t('common.confirm') || 'Confirmar'}
        message={getConfirmMessage()}
        onAccept={executeConfirm}
        onClose={() => { setConfirmAction(null); setConfirmTarget(null); }}
        showCancel={true}
      />
    </div>
  );
};